import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  UpdateStakeholderDto,
  StakeholderDto as ApiStakeholderDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";
import { ACTION_GET_STAKEHOLDERS } from "../GetDMStakeholders/useGetDMStakeholders";

import { roleGuard } from "@api";
import { doubleMaterialityStakeholdersApi } from "@api/services";

export const ACTION_UPDATE_STAKEHOLDER = "UPDATE_STAKEHOLDER";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface UpdateStakeholderResponse {
  data: ApiStakeholderDto;
}

const updateStakeholder = async (
  data: UpdateStakeholderDto & { id: string },
): Promise<UpdateStakeholderResponse> => {
  const response =
    await doubleMaterialityStakeholdersApi.doubleMaterialityStakeholdersControllerUpdateStakeholder(
      data.id,
      data,
    );

  return {
    data: response.data,
  };
};

export const useUpdateStakeholder = (
  user: UserProfileDto | null,
): {
  updateStakeholder: (data: UpdateStakeholderDto & { id: string }) => void;
  updateStakeholderAsync: (
    data: UpdateStakeholderDto & { id: string },
  ) => Promise<UpdateStakeholderResponse>;
  isPendingUpdateStakeholder: boolean;
  isSuccessUpdateStakeholder: boolean;
  isErrorUpdateStakeholder: boolean;
  errorUpdateStakeholder: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: updateStakeholder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_STAKEHOLDERS],
      });
      enqueueSnackbar(t("stakeholders.messages.updateSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("stakeholders.messages.updateError"), {
        variant: "error",
      });
    },
  });

  return {
    updateStakeholder: mutation.mutate,
    updateStakeholderAsync: mutation.mutateAsync,
    isPendingUpdateStakeholder: mutation.isPending,
    isSuccessUpdateStakeholder: mutation.isSuccess,
    isErrorUpdateStakeholder: mutation.isError,
    errorUpdateStakeholder: mutation.error,
    enabled,
  };
};
